import { ApiRoute, HttpMethod } from '../../../common/api/routes';
import { GoogleMyBusiness } from '../../constants';
import { LocationId } from '../../../location/Location';
import { WorkflowStartResult } from '../../../external/aws/stepFunctions';
import { IPublishImageRequest } from '../../../publish/image';
import { UploadId } from '../../../upload/Upload';
import { IAuditData } from '../../../publish/audit';
import { GoogleMyBusinessLocationAddress } from './models/businessLocation';
import { CallToAction, Offer, PostTopicType } from './models/postModels';
import { GoogleMyBusinessAccountResourceId } from './resource-identifiers';

export const MAX_SUMMARY_LENGTH = 1500;

export type GetGoogleMyBusinessLocationsQueryParameters = {
    locationId: LocationId;
    accountId: GoogleMyBusinessAccountResourceId;
};

export type GetGoogleMyBusinessAccountNameAndLocationNameAndAddressResponse = {
    accountName: string;
    address?: GoogleMyBusinessLocationAddress;
    businessLocationName: string;
};

export interface GoogleMyBusinessPublishData {
    type: PostTopicType;
    summary?: string;
    callToAction?: CallToAction;
    event?: {
        title: string;
        start: Date;
        end: Date;
    };
    offer?: Offer;
    scheduledTime?: Date | null;
}

export type PublishImageToGoogleMyBusinessWorkflowInput = {
    channelData: GoogleMyBusinessPublishData;
    uploadId: UploadId;
    auditData: IAuditData;
    scheduledTime: Date;
};

export const GoogleMyBusinessEndpoints = {
    canPostForLocation: new ApiRoute<{ result: boolean }, { locationId: LocationId }, void, void>(
        HttpMethod.Get,
        path =>
            path
                .literal('publish')
                .literal(GoogleMyBusiness.name)
                .literal('location')
                .param('locationId')
                .literal('hasAccess'),
    ),
    getConfiguredAccountNameAndLocationNameAndAddress: new ApiRoute<
        GetGoogleMyBusinessAccountNameAndLocationNameAndAddressResponse,
        { locationId: LocationId },
        void,
        void
    >(HttpMethod.Get, path =>
        path.literal('integration').literal(GoogleMyBusiness.name).literal('location').param('locationId'),
    ),
    publishPhoto: new ApiRoute<
        WorkflowStartResult,
        { locationId: LocationId },
        IPublishImageRequest<GoogleMyBusinessPublishData>,
        void
    >(HttpMethod.Post, path =>
        path.literal('publish').literal(GoogleMyBusiness.name).literal('location').param('locationId').literal('photo'),
    ),
};

// Report Options
export interface GoogleMyBusinessReportWithStartAndEndOptions {
    startDate: Date;
    endDate: Date;
}

export type GoogleMyBusinessReportSearchOptions = GoogleMyBusinessReportWithStartAndEndOptions;

export type GoogleMyBusinessReportViewOptions = GoogleMyBusinessReportWithStartAndEndOptions;

export type GoogleMyBusinessReportDirectionsOptionsPeriod = 'NINETY' | 'SEVEN' | 'THIRTY';
export interface GoogleMyBusinessReportDirectionsOptions {
    period: GoogleMyBusinessReportDirectionsOptionsPeriod;
}

export type GoogleMyBusinessReportPhotoViewOptions = GoogleMyBusinessReportWithStartAndEndOptions;

export type GoogleMyBusinessReportPhotoQuantityOptions = GoogleMyBusinessReportWithStartAndEndOptions;

export interface GoogleMyBusinessReportRecentPostsOptions {
    count?: number;
}

export interface GoogleMyBusinessReportTopReviewsOptions {
    count?: number;
}

// Report Return Values
export interface GoogleMyBusinessReportAggregatedNumberDataValue {
    date: Date;
    value: number;
}

export interface GoogleMyBusinessReportAggregatedNumberData {
    total: number;
    values: GoogleMyBusinessReportAggregatedNumberDataValue[];
}

// Report returns
export interface GoogleMyBusinessReportSearchData {
    direct: number;
    discovery: number;
    brand: number;
}

export type GoogleMyBusinessReportViewAggregatedNumberData = GoogleMyBusinessReportAggregatedNumberData;

export interface GoogleMyBusinessReportViewData {
    visits: GoogleMyBusinessReportViewAggregatedNumberData;
    directions: GoogleMyBusinessReportViewAggregatedNumberData;
    calls: GoogleMyBusinessReportViewAggregatedNumberData;
}

export interface GoogleMyBusinessReportDirectionsLocationData {
    title: string;
    total: number;
    latitude?: number;
    longitude?: number;
}

export interface GoogleMyBusinessReportDirectionsData {
    all: GoogleMyBusinessReportDirectionsLocationData[];
}

export type GoogleMyBusinessReportPhotoViewAggregatedNumberData = GoogleMyBusinessReportAggregatedNumberData;

export interface GoogleMyBusinessReportPhotoViewData {
    merchant: GoogleMyBusinessReportPhotoViewAggregatedNumberData;
    others: GoogleMyBusinessReportPhotoViewAggregatedNumberData;
}

export interface GoogleMyBusinessReportPhotoQuantityDataValue {
    totalUploads: number;
    totalViews: number;
}

export interface GoogleMyBusinessReportPhotoQuantityData {
    merchant: GoogleMyBusinessReportPhotoQuantityDataValue;
    others: GoogleMyBusinessReportPhotoQuantityDataValue;
}

export interface GoogleMyBusinessReportRecentPost {
    description: string;
    thumbnailUrl?: string;
    linkUrl: string;

    totalViews: number;
    totalClicks: number;

    createdAt: Date;
    updatedAt: Date;
}

export interface GoogleMyBusinessReportRecentPostsData {
    posts: GoogleMyBusinessReportRecentPost[];
}

export interface GoogleMyBusinessReportReviewer {
    displayName: string;
    profilePhotoUrl: string;
}

export interface GoogleMyBusinessReportReviewReply {
    comment: string;
    updatedAt: Date;
}

export interface GoogleMyBusinessReportReview {
    id: string;

    rating: number;
    comment?: string;

    reviewer?: GoogleMyBusinessReportReviewer;
    reviewReply?: GoogleMyBusinessReportReviewReply;

    createdAt: Date;
    updatedAt: Date;
}

export interface GoogleMyBusinessReportTopReviewsData {
    reviews: GoogleMyBusinessReportReview[];
    averageRating: number;
    totalReviews: number;
}
